/**
 * EcdysisSkillEffect — Poison tree Expel path, Fork 2 active.
 *
 * Sheds the player's accumulated toxin load as a point-blank burst. Burst
 * damage scales with two factors: the total remaining poison DoT across all
 * poisoned enemies within range, and the player's current toxinLoad resource
 * (from the Expel aura). After the burst fires, toxinLoad resets to zero —
 * a clean slate for the next cycle.
 *
 * The burst hits every enemy within radius, poisoned or not. The more DoT
 * already seeded into the crowd, the bigger the detonation. Poison is NOT
 * removed from enemies (unlike Entropy, which cashes in individual DoT).
 *
 * Cooldown tier: long (2000ms).
 *
 * Placeholder damage scalars pending design decision — see spec Discrepancy
 * Log for the base/poison/toxinLoad scalar values.
 *
 * Pure logic — no Phaser dependencies.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see ADR-066 (source-tagged DoT coexistence)
 * @see poison-spec.md § Ecdysis (ecdysis)
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Minimal enemy representation for targeting. */
export interface EcdysisTarget {
  readonly entityId: string;
  readonly position: { x: number; y: number };
}

/** Poison instance data needed for burst damage computation. */
export interface EcdysisPoisonInstance {
  readonly intensity: number;
  readonly remainingMs: number;
  readonly stacks: number;
  readonly tickIntervalMs: number;
}

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

/** Minimal ZoneConfig subset for the zone creation call. */
export interface EcdysisZoneConfig {
  id: string;
  type: string;
  position: { x: number; y: number };
  durationMs: number;
  sourceSkillId: string;
  shape: 'circle';
  radiusTiles: number;
  dotDefId: string;
  dotIntensity: number;
  tickIntervalMs: number;
  oneShot: boolean;
}

export interface EcdysisDeps {
  readonly targeting: {
    getPlayerPosition(): { x: number; y: number };
    getEnemiesInRange(
      center: { x: number; y: number },
      rangeTiles: number,
    ): ReadonlyArray<EcdysisTarget>;
  };
  readonly statusEffects: {
    hasPoisonStatus(entityId: string): boolean;
    getPoisonInstances(entityId: string): ReadonlyArray<EcdysisPoisonInstance>;
  };
  readonly damage: {
    applyBurst(entityId: string, amount: number, sourceSkillId: string): void;
  };
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
    hasEffect(skillId: string, key: string): boolean;
    getTreePassiveEffect(
      treeName: string,
      key: string,
      aggregation: string,
    ): number;
  };
  readonly expel: {
    getToxinLoad(): number;
    consumeToxinLoad(): void;
    isActive(): boolean;
    forceReactivate(): void;
  };
  readonly zoneSystem: {
    create(config: EcdysisZoneConfig): string;
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface EcdysisConfig {
  /** Cooldown in ms (long tier = 2000). */
  readonly cooldownMs: number;
  /** Base burst radius in tiles. Catharsis adds burstRadiusTiles via resolver. */
  readonly burstRadiusTiles: number;
  /** Flat base damage before scaling factors. Placeholder pending design. */
  readonly baseDamage: number;
  /** Scalar applied to total remaining poison damage across all targets. */
  readonly poisonDamageScalar: number;
  /** Scalar applied to current toxinLoad value. */
  readonly toxinLoadScalar: number;
}

const DEFAULT_CONFIG: EcdysisConfig = {
  cooldownMs: 2000,              // long tier
  burstRadiusTiles: 1.5,         // spec suggestion — pending design confirmation
  baseDamage: 5,                 // placeholder pending design
  poisonDamageScalar: 0.5,       // placeholder pending design
  toxinLoadScalar: 0.3,          // placeholder pending design
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class EcdysisSkillEffect implements SkillEffect {
  readonly skillId = 'ecdysis';
  readonly type = 'active' as const;
  readonly activationMode = 'instant' as const;

  private readonly deps: EcdysisDeps;
  private readonly config: EcdysisConfig;

  private _cooldownRemaining = 0;
  private _activationsCount = 0;
  private _lastBurstDamage = 0;
  private _residueCount = 0;
  private _lastResidueSpawned = false;

  constructor(deps: EcdysisDeps, config: Partial<EcdysisConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters ---

  get cooldown(): number { return this._cooldownRemaining; }
  get activationsCount(): number { return this._activationsCount; }
  /** Damage dealt by the most recent burst (for test assertions). */
  get lastBurstDamage(): number { return this._lastBurstDamage; }
  /** Whether the most recent activation spawned a Residue zone. */
  get lastResidueSpawned(): boolean { return this._lastResidueSpawned; }

  // --- SkillEffect interface ---

  canActivate(): boolean {
    return this._cooldownRemaining <= 0;
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    // Second Skin: snapshot Expel state before the burst fires.
    const wasExpelActive = this.deps.expel.isActive();

    const playerPos = this.deps.targeting.getPlayerPosition();
    const effectiveRadius = this.resolveRadius();
    const enemies = this.deps.targeting.getEnemiesInRange(
      playerPos,
      effectiveRadius,
    );

    // Compute combined burst damage from all poison in range + toxinLoad.
    const burstDamage = this.computeBurstDamage(enemies);
    this._lastBurstDamage = burstDamage;

    // Deal the burst to every enemy in radius — poisoned or not.
    if (burstDamage > 0) {
      for (const enemy of enemies) {
        this.deps.damage.applyBurst(enemy.entityId, burstDamage, 'ecdysis');
      }
    }

    // Consume toxinLoad — the shed is complete.
    this.deps.expel.consumeToxinLoad();

    // Second Skin: if Expel was active before the burst and the upgrade is
    // unlocked, reactivate Expel immediately at no resource cost. The aura
    // comes back up before the next frame — no gap in the cycle.
    if (
      wasExpelActive &&
      this.deps.resolver.hasEffect('ecdysis_upgrade_2', 'expelImmediateReactivation')
    ) {
      this.deps.expel.forceReactivate();
    }

    // Residue: leave a contact toxin patch at the detonation point. One dose
    // per enemy who walks through (oneShot). Handles reinforcements who come
    // to investigate the burst.
    this.spawnResidue(playerPos);

    this._cooldownRemaining = this.config.cooldownMs;
    this._activationsCount++;
    return true;
  }

  deactivate(): void {
    // Instant activation — no toggle state.
  }

  isActive(): boolean {
    return false;
  }

  update(delta: number): void {
    if (this._cooldownRemaining > 0) {
      this._cooldownRemaining = Math.max(0, this._cooldownRemaining - delta);
    }
  }

  destroy(): void {
    this._cooldownRemaining = 0;
  }

  // ---------------------------------------------------------------------------
  // Private helpers
  // ---------------------------------------------------------------------------

  /**
   * Resolve the effective burst radius: base + Catharsis bonus.
   * Catharsis (ecdysis_upgrade_1) registers burstRadiusTiles as additive.
   */
  private resolveRadius(): number {
    const bonus = this.deps.resolver.getAdditiveEffect(
      'ecdysis',
      'burstRadiusTiles',
    );
    return this.config.burstRadiusTiles + bonus;
  }

  /**
   * Residue passive: spawn a contact_coating zone at the detonation point.
   * One dose per enemy (oneShot). Only fires if the passive is unlocked —
   * checked via resolver.hasEffect on ecdysis_passive / residueRadiusTiles.
   */
  private spawnResidue(playerPos: { x: number; y: number }): void {
    if (!this.deps.resolver.hasEffect('ecdysis_passive', 'residueRadiusTiles')) {
      this._lastResidueSpawned = false;
      return;
    }

    const radiusTiles = this.deps.resolver.getAdditiveEffect(
      'ecdysis_passive',
      'residueRadiusTiles',
    );
    const durationMs = this.deps.resolver.getAdditiveEffect(
      'ecdysis_passive',
      'residueDurationMs',
    );
    // Base poison intensity — same as the tree's default (3).
    const dotIntensity = 3;

    const zoneId = `ecdysis_residue_${this._residueCount++}`;
    this.deps.zoneSystem.create({
      id: zoneId,
      type: 'contact_coating',
      position: { x: playerPos.x, y: playerPos.y },
      durationMs: durationMs > 0 ? durationMs : 4000,
      sourceSkillId: 'ecdysis',
      shape: 'circle',
      radiusTiles: radiusTiles > 0 ? radiusTiles : 1,
      dotDefId: 'poison',
      dotIntensity,
      tickIntervalMs: 250,
      oneShot: true,
    });
    this._lastResidueSpawned = true;
  }

  /**
   * Compute burst damage from two scaling sources:
   *
   * 1. **Remaining poison** — total damage potential across all poisoned enemies
   *    in range: sum of intensity × stacks × (remainingMs / tickIntervalMs)
   *    per instance, scaled by poisonDamageScalar.
   *
   * 2. **toxinLoad** — the player's current Expel resource, scaled by
   *    toxinLoadScalar.
   *
   * Both feed into a single value: `baseDamage + poisonTotal × scalar +
   * toxinLoad × scalar`, then multiplied by Toxicologist if unlocked.
   */
  private computeBurstDamage(
    enemies: ReadonlyArray<EcdysisTarget>,
  ): number {
    // Sum remaining poison damage across all enemies in range.
    let totalPoisonDamage = 0;
    for (const enemy of enemies) {
      if (!this.deps.statusEffects.hasPoisonStatus(enemy.entityId)) continue;
      const instances = this.deps.statusEffects.getPoisonInstances(
        enemy.entityId,
      );
      for (const inst of instances) {
        if (inst.remainingMs <= 0) continue;
        totalPoisonDamage +=
          inst.intensity * inst.stacks * (inst.remainingMs / inst.tickIntervalMs);
      }
    }

    const toxinLoad = this.deps.expel.getToxinLoad();

    let burst =
      this.config.baseDamage +
      totalPoisonDamage * this.config.poisonDamageScalar +
      toxinLoad * this.config.toxinLoadScalar;

    // Toxicologist multiplier — tree-wide passive boosting all poison damage.
    const multiplier = this.deps.resolver.getTreePassiveEffect(
      'poison',
      'dotDamageMultiplier',
      'product',
    );
    burst *= multiplier;

    return burst;
  }
}
